/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";
import Pagination from "../../elements/Pagination";
import { debounce } from 'lodash';

export default function PujaService() {
  const [service, setService] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getPujaList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.get(`service?page=${currentPage + 1}&limit=10&includeInactive=true`);
      if (response?.result) {
        setService(response.result.data);
        setPageCount(response.result.totalPages);
      } else {
        setService([]);
        setPageCount(0);
      }
    } catch (err) {
      toast.error('Failed to fetch puja list');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const searchPuja = useCallback(async (term) => {
    setLoading(true);
    try {
      const response = await ApiService.get(`service/search?searchTerm=${term}&page=${currentPage + 1}&limit=10`);
      if (response?.result) {
        setService(response.result.data);
        setPageCount(response.result.totalPages);
      } else {
        setService([]);
        setPageCount(0);
      }
    } catch (err) {
      toast.error('Failed to search puja');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  // Debounced search function
  const debouncedSearch = React.useMemo(() => debounce((value) => {
    searchPuja(value);
  }, 500), [searchPuja]);

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
    } else {
      getPujaList();
    }
  }, [currentPage, searchTerm, debouncedSearch, getPujaList]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value) {
      getPujaList();
    }
  };

  const handlePageClick = (selectedPage) => {
    const clickedPage = selectedPage.selected;
    if (clickedPage !== currentPage) {
      setCurrentPage(clickedPage);
    }
  };

  const handleToggleStatus = async (id, newStatus) => {
    try {
      await ApiService.put(`service/${id}/toggle-status`, { isActive: newStatus });
      // Refresh the list after toggling
      if (searchTerm) {
        searchPuja(searchTerm);
      } else {
        getPujaList();
      }
      toast.success(`Puja ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      toast.error('Failed to update status');
      console.error('Toggle status error:', err);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        {loading && <Loader />}
        <div className="card dz-card" id="bootstrap-table2">
          <div className="card-header flex-wrap d-flex justify-content-between border-0">
            <div>
              <h4 className="card-title">Puja List</h4>
            </div>
            <div className="d-flex align-items-center">
              <div className="input-group search-area" style={{ width: '300px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search puja..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <span className="input-group-text">
                  <i className="flaticon-381-search-2"></i>
                </span>
              </div>
              <Link to="/add-puja" className="btn btn-primary ms-3">
                Add Puja
              </Link>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-responsive-md">
                <thead>
                  <tr>
                    <th>
                      <strong>Sr No.</strong>
                    </th>
                    <th>
                      <strong>Puja Name</strong>
                    </th>
                    <th>
                      <strong>Price</strong>
                    </th>
                    <th>
                      <strong>Variants</strong>
                    </th>
                    <th>
                      <strong>Status</strong>
                    </th>
                    <th>
                      <strong>Action</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {service && service?.length > 0 ? (
                    service.map((dp, key) => (
                      <tr key={key}>
                        <td>
                          <strong>{key + 1}</strong>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="w-space-no">{dp.title}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="w-space-no">₹{dp.price}</span>
                          </div>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.variantCount > 0 ? dp.variantCount : '-'}
                          </span>
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={dp?.isActive}
                              onChange={() => handleToggleStatus(dp.id, !dp.isActive)}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        </td>
                        <td>
                          <Link
                            to={`/edit-puja/${dp?.id}`}
                            className="btn btn-primary shadow btn-xs sharp me-1"
                          >
                            <i className="fa fa-pencil" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No puja services found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {!searchTerm && <Pagination pageCount={pageCount} handlePageClick={handlePageClick} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
